// The rail's workspace menu as rows (02-DESIGN/03-console/01-the-console.md
// § the shape: the workspace menu; hq decision 0069 point 5): one row per
// membership — the name and the role, the current one marked — then, where
// this workspace manages workspaces and the person is its admin, *Managed by
// Acme* and one row per child; then *All workspaces*, and *Create a
// workspace* where sign-up is allowed, for every sign-in under the platform
// provider, one membership or many: both are reached on the identity plane,
// asked for with `-` as the tenant (hq decision 0072). A workspace signed in through its own
// provider has no memberships to list: its menu is the current workspace,
// its children, and *Sign in somewhere else*. The pure part, drawn by the rail.

import { workspaceName } from "../platform/names.ts";
import type { Child } from "../wire/auth.ts";
import type { Membership } from "../wire/identity.ts";

/** MenuRow is a workspace the person may move to. */
export interface MenuRow {
  slug: string;
  name: string;
  /** The person's role there, for a membership; a child's row names none. */
  role?: string;
  current: boolean;
}

/** MenuFoot is one of the menu's last entries. */
export type MenuFoot = "all" | "create" | "elsewhere";

/** MenuShape is the menu, top to bottom. */
export interface MenuShape {
  memberships: MenuRow[];
  /** *Managed by Acme* and its rows, where there is a child to list. */
  managed?: { heading: string; rows: MenuRow[] };
  foot: MenuFoot[];
}

/** WORDS are the menu's own entries, in the design's words. */
export const FOOT_WORDS: Readonly<Record<MenuFoot, string>> = {
  all: "All workspaces",
  create: "Create a workspace",
  elsewhere: "Sign in somewhere else",
};

/**
 * workspaceMenu builds the menu's rows. `memberships` is the plane's reply,
 * or undefined while it is not read (or where it could not be): the current
 * workspace then stands alone, and is always listed, marked, whatever the
 * reply says. `children` is the children list, read only for an admin of a
 * workspace that manages workspaces; an empty one draws no heading.
 */
export function workspaceMenu(input: {
  current: { slug: string; name: string; role?: string };
  onPlatform: boolean;
  signUp: boolean;
  memberships?: readonly Membership[];
  children?: readonly Child[];
}): MenuShape {
  const { current, onPlatform } = input;
  const here: MenuRow = { slug: current.slug, name: current.name, current: true };
  if (current.role !== undefined) {
    here.role = current.role;
  }
  const memberships: MenuRow[] = [];
  if (onPlatform && input.memberships !== undefined) {
    for (const m of input.memberships) {
      memberships.push(
        m.tenant === current.slug
          ? { ...here, role: m.role }
          : { slug: m.tenant, name: workspaceName(m.tenant, m.name), role: m.role, current: false },
      );
    }
  }
  if (!memberships.some((r) => r.current)) {
    memberships.unshift(here);
  }
  const foot: MenuFoot[] = [];
  if (onPlatform) {
    foot.push("all");
    if (input.signUp) {
      foot.push("create");
    }
  } else {
    foot.push("elsewhere");
  }
  const shape: MenuShape = { memberships, foot };
  const children = input.children ?? [];
  if (children.length > 0) {
    shape.managed = {
      heading: `Managed by ${current.name}`,
      rows: [...children]
        .map((c) => ({ slug: c.slug, name: workspaceName(c.slug, c.name), current: false }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }
  return shape;
}
